import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Button, Dialog, TextField, MenuItem, Alert, Chip, IconButton, Typography, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { moduleAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SuperAdminDataTable } from '../../components/superadmin/SuperAdminDataTable';
import { FIELD_TYPE_CATEGORIES, FIELD_TYPE_OPTIONS } from '../../constants/fieldTypes';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const STATE_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: brandCss.green100, color: brandCss.primaryDark },
  hidden: { bg: '#fef3c7', color: '#d97706' },
  disabled: { bg: '#e0e7ff', color: '#4338ca' },
  deleted: { bg: '#fee2e2', color: '#dc2626' },
};

export const FieldManagementPage: React.FC = () => {
  const { moduleKey } = useParams<{ moduleKey: string }>();
  const [mod, setMod] = useState<any>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: '', type: 'text', required: false });
  const [message, setMessage] = useState('');

  const load = async () => {
    if (!moduleKey) return;
    const [mods, f] = await Promise.all([
      moduleAPI.getAll(),
      moduleAPI.getFields(moduleKey),
    ]);
    setMod(mods.data.find((m: any) => m.key === moduleKey));
    setFields(f.data);
  };

  useEffect(() => { load(); }, [moduleKey]);

  const setState = async (fieldKey: string, fieldState: string) => {
    await moduleAPI.setFieldState(moduleKey!, fieldKey, fieldState);
    setMessage(`Field marked as ${fieldState}`);
    load();
  };

  const handleAdd = async () => {
    await moduleAPI.addField(moduleKey!, form);
    setOpen(false);
    setForm({ label: '', type: 'text', required: false });
    setMessage('Field added');
    load();
  };

  if (!mod) return null;

  return (
    <Box>
      <PageHeader
        title={`Field Management — ${mod.label}`}
        subtitle={getPageSubtitle('field_management')}
        icon={<TuneIcon />}

        action={
          !mod.isCore && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={catchyPrimaryButtonSx}>
              Add Field
            </Button>
          )
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <SuperAdminDataTable
        rows={fields}
        recordLabel="fields"
        searchPlaceholder="Search fields..."
        statusOptions={[{ label: 'All', value: 'all' }]}
        columns={[
          { id: 'label', header: 'Label', searchValue: (f) => `${f.label} ${f.key}`, render: (f) => <strong>{f.label}</strong> },
          { id: 'key', header: 'Key', render: (f) => <code style={{ fontSize: 12 }}>{f.key}</code> },
          { id: 'type', header: 'Type', render: (f) => f.type },
          {
            id: 'state',
            header: 'State',
            render: (f) => {
              const st = f.fieldState || 'active';
              const c = STATE_COLORS[st] || STATE_COLORS.active;
              return <Chip label={st} size="small" sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 11, textTransform: 'capitalize' }} />;
            },
          },
        ]}
        renderActions={(f) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" title="Hide" onClick={() => setState(f.key, 'hidden')}><VisibilityOffIcon fontSize="small" /></IconButton>
            <IconButton size="small" title="Disable (read-only)" onClick={() => setState(f.key, 'disabled')}><BlockIcon fontSize="small" /></IconButton>
            <IconButton size="small" title="Activate" onClick={() => setState(f.key, 'active')} sx={{ color: brandCss.primary }}>✓</IconButton>
            {!f.isCoreField && (
              <IconButton size="small" title="Delete" onClick={() => setState(f.key, 'deleted')}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton>
            )}
          </Box>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: `${RADIUS}px`, p: 3, minWidth: 400 } }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Add Custom Field</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField label="Label" fullWidth size="small" sx={catchyTextFieldSx} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></Grid>
          <Grid item xs={12}>
            <TextField select label="Type" fullWidth size="small" sx={catchyTextFieldSx} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {FIELD_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><Button variant="contained" fullWidth onClick={handleAdd} sx={catchyPrimaryButtonSx}>Add Field</Button></Grid>
        </Grid>
      </Dialog>
    </Box>
  );
};
